import React, { useRef } from 'react';
import { sendGAEvent } from '@noths/polaris-client-google-analytics';
import { visuallyHidden } from '@noths/polaris-client-styles';

import { ErrorNotification } from 'src/components/atoms/ErrorNotification/ErrorNotification';
import { PromoCodeMessageButton } from 'src/components/atoms/PromoCodeMessageButton/PromoCodeMessageButton';
import { BasketCTAButtonContainer } from 'src/components/molecules/BasketCTAButton/BasketCTAButtonContainer';
import { MAIN_PAGE_CTA_ID } from 'src/components/molecules/BasketCTAButton/elementIDs';
import { BreadcrumbContainer } from 'src/components/molecules/Breadcrumbs/containers/BreadcrumbsContainer';
import { DeliveryEventContainer } from 'src/components/molecules/DeliveryEvent/containers/DeliveryEventContainer';
import { ExpandablePanel } from 'src/components/molecules/ExpandablePanel/ExpandablePanel';
import { InventoryStatusLabel } from 'src/components/molecules/InventoryStatusLabel/InventoryStatusLabel';
import { LazyRender } from 'src/components/molecules/LazyRender/LazyRender';
import { MembershipBannerSection } from 'src/components/molecules/MembershipBannerSection/MembershipBannerSection';
import { PartnerMaxiModuleWithTracking } from 'src/components/molecules/PartnerMaxiModule/components/PartnerMaxiModuleWithTracking';
import { PartnerMiniModule } from 'src/components/molecules/PartnerMiniModule/PartnerMiniModule';
import { ProductFeaturesWithTracking } from 'src/components/molecules/ProductFeatures/components/ProductFeaturesWithTracking';
import { getTopProductFeatures } from 'src/components/molecules/ProductFeatures/utils/getTopProductFeatures';
import { ProductPriceAndRatingWithTracking } from 'src/components/molecules/ProductPriceAndRating/ProductPriceAndRatingWithTracking';
import { ProductRatingMaxiSummary } from 'src/components/molecules/ProductRatingMaxiSummary/ProductRatingMaxiSummary';
import { StickySection } from 'src/components/molecules/StickySection/components/StickySection';
import { TrustCarouselWithTracking } from 'src/components/molecules/TrustCarousel/components/TrustCarouselWithTracking';
import { DeliverySectionContainer } from 'src/components/organisms/DeliverySection/containers/DeliverySectionContainer/DeliverySectionContainer';
import { LinkCarouselContainer } from 'src/components/organisms/LinkCarousel/containers/LinkCarouselContainer';
import { ProductDescriptionContainer } from 'src/components/organisms/ProductDescription/containers/ProductDescriptionContainer';
import { ProductImagesCarouselContainer } from 'src/components/organisms/ProductImagesCarousel';
import { ProductOptionsContainer } from 'src/components/organisms/ProductOptions/ProductOptionsContainer';
import { ProductReviewsContainer } from 'src/components/organisms/ProductReviews/containers/ProductReviewsContainer';
import { RecommendedProductsCarouselContainer } from 'src/components/organisms/RecommendedProductsCarousel/containers/RecommendedProductsCarouselContainer';
import { Placement } from 'src/constants/recommendations';
import { REVIEWS_URL_FRAGMENT_ID } from 'src/fragmentIDs';
import { isOutOfStock, isSuspended } from 'src/utils/inventoryStatus';
import { useEditProductPersonalisationOptions } from '../hooks/useEditProductPersonalisationOptions';
import {
  DELIVERY_HEADING,
  getOptionsHeadingText,
  NEW_PRODUCT_LABEL,
  PRODUCT_DETAILS_HEADING,
} from './copy';
import { PARTNER_MODULE_ID } from './elementIds';
import { useStandardLayout } from './useStandardLayout';
import * as styles from './StandardLayout.styles';

const RECOMMENDED_PRODUCTS_LAZY_LOAD_MARGIN = '200px';

interface StandardLayoutProps {
  partnerRating?: number | null;
  partnerReviewCount?: number;
}

export const StandardLayout = ({ partnerRating, partnerReviewCount }: StandardLayoutProps) => {
  const {
    basketMutationError,
    openContactPartnerModal,
    optionsCostRange,
    pageLiveRegionText,
    partnerHref,
    product,
    promoCodeMessage,
    reviews,
  } = useStandardLayout();
  const formRef = useRef<HTMLFormElement>(null);
  const optionsHeading = getOptionsHeadingText(
    product.purchasable,
    product.options.length > 0,
    product.specialFeatures.personalisable,
  );
  const suspended = isSuspended(product.availability, product.available);

  const { allowsGiftWrap, options, specialFeatures } = product;
  const personalisationAddsCost =
    options.length > 0 && specialFeatures.personalisable && optionsCostRange.minRequired > 0;
  const minimumPersonalisationCost = personalisationAddsCost ? optionsCostRange.minRequired : 0;
  const priceIsMinimum = optionsCostRange.maxOptional + optionsCostRange.maxRequired > 0;
  const productFeatures = getTopProductFeatures({
    allowsGiftWrap,
    productRating: reviews.averageProductRating,
    specialFeatures,
  });

  useEditProductPersonalisationOptions();

  return (
    <>
      <div css={visuallyHidden} role="status">
        {pageLiveRegionText}
      </div>
      <div css={styles.wrapper}>
        <div css={[styles.maxWidthContainer, styles.breadcrumbsContainer]}>
          <BreadcrumbContainer />
        </div>

        {/* start grid containing a column that spans across rows */}
        <div css={[styles.maxWidthContainer, styles.grid, styles.largeSpacingBottom]}>
          <h1 css={[styles.narrowColumn, styles.productTitle]}>{product.title}</h1>
          <div css={styles.narrowColumn}>
            <PartnerMiniModule
              linkHref={`#${PARTNER_MODULE_ID}`}
              location={product.partner.location}
              logoSrc={product.partner.logo}
              name={product.partner.name}
              partnerRating={partnerRating || undefined}
              partnerReviewCount={partnerReviewCount}
            />
          </div>
          <div css={[styles.widerColumn, styles.spanAcrossRows]}>
            <ProductImagesCarouselContainer />
          </div>
          <div css={styles.narrowColumn}>
            <ProductPriceAndRatingWithTracking
              averageRating={reviews.averageProductRating || undefined}
              numberOfReviews={reviews.numberOfReviews || 0}
              preSalePrices={product.preSalePrices}
              price={product.price}
              priceIsMinimum={priceIsMinimum}
              reviewsLinkHref={`#${REVIEWS_URL_FRAGMENT_ID}`}
              salePercentage={product.salePercentage || 0}
            />
            {promoCodeMessage && (
              <PromoCodeMessageButton promoCode={promoCodeMessage.promoCode}>
                {promoCodeMessage.message}
              </PromoCodeMessageButton>
            )}
            <InventoryStatusLabel inventoryStatus={product.inventoryStatus} suspended={suspended} />
            <DeliveryEventContainer />
            {product.isNew && <p css={visuallyHidden}>{NEW_PRODUCT_LABEL}</p>}
          </div>
          {productFeatures.length > 0 && (
            <div css={styles.narrowColumn}>
              <ProductFeaturesWithTracking
                currency={product.price.currency}
                minimumPersonalisationCost={minimumPersonalisationCost}
                productFeatures={productFeatures}
              />
            </div>
          )}
          <div css={styles.narrowColumn}>
            <ErrorNotification returnFocusToRef={formRef} shouldShow={basketMutationError} />
            {optionsHeading && <h2 css={styles.sectionHeading}>{optionsHeading}</h2>}
            <ProductOptionsContainer ref={formRef} />
            <BasketCTAButtonContainer />
          </div>
          {!isOutOfStock(product.inventoryStatus) && (
            <div css={styles.narrowColumn}>
              <h2 css={styles.sectionHeading}>{DELIVERY_HEADING}</h2>
              <DeliverySectionContainer />
            </div>
          )}
        </div>
        {/* end grid containing a column that spans across rows */}

        <div css={[styles.maxWidthContainer, styles.largeSpacingBottom]}>
          <ExpandablePanel
            collapseButtonText="Read less"
            expandButtonText="Read more"
            onToggle={(expanded) =>
              sendGAEvent({
                event: 'custom_event',
                event_category: PRODUCT_DETAILS_HEADING,
                event_action: expanded ? 'Expand' : 'Collapse',
                event_label: 'not set',
              })
            }
          >
            <h2 css={styles.heading}>{PRODUCT_DETAILS_HEADING}</h2>
            <ProductDescriptionContainer />
          </ExpandablePanel>
        </div>

        <div
          css={[styles.maxWidthContainer, styles.largeSpacingBottom]}
          id={REVIEWS_URL_FRAGMENT_ID}
        >
          <div css={styles.overallRatingWrapper}>
            <ProductRatingMaxiSummary
              averageRating={reviews.averageProductRating || null}
              numberOfReviews={reviews.numberOfReviews || 0}
            />
          </div>
          <div css={styles.productReviewsWrapper}>
            <ProductReviewsContainer />
          </div>
        </div>

        <div css={[styles.maxWidthContainer, styles.largeSpacingBottom]}>
          <LazyRender
            options={{
              rootMargin: RECOMMENDED_PRODUCTS_LAZY_LOAD_MARGIN,
            }}
          >
            <RecommendedProductsCarouselContainer placementType={Placement.SimilarProducts} />
          </LazyRender>
        </div>

        <div css={[styles.maxWidthContainer, styles.largeSpacingBottom]}>
          <TrustCarouselWithTracking />
        </div>

        <div css={[styles.partnerModules]} id={PARTNER_MODULE_ID}>
          <div css={[styles.maxWidthContainer, styles.largeSpacingBottom]}>
            <PartnerMaxiModuleWithTracking
              location={product.partner.location}
              logoSrc={product.partner.logo}
              name={product.partner.name}
              onContactPartnerClick={openContactPartnerModal}
              partnerLinkHref={partnerHref}
              partnerRating={partnerRating || undefined}
              partnerReviewCount={partnerReviewCount}
              partnerShortcode={product.partner.shortcode}
            />
            <div css={[styles.partnerProductsCarousel]}>
              <LazyRender
                options={{
                  rootMargin: RECOMMENDED_PRODUCTS_LAZY_LOAD_MARGIN,
                }}
              >
                <RecommendedProductsCarouselContainer placementType={Placement.SameSeller2} />
              </LazyRender>
            </div>
          </div>
        </div>
        <div css={[styles.maxWidthContainer, styles.largeSpacingBottom]}>
          <MembershipBannerSection />
        </div>
        <div css={[styles.maxWidthContainer, styles.largeSpacingBottom]}>
          <LinkCarouselContainer />
        </div>
        <StickySection triggerElementId={MAIN_PAGE_CTA_ID}>
          <BasketCTAButtonContainer />
        </StickySection>
      </div>
    </>
  );
};
